import { useState, useCallback, useRef, useEffect } from "react";
import axios from "../../../config/axios";
import useIdleState from "./useIdleState";
import BeaconSubCheck from "./BeaconSubCheck";
import BeaconPairingCode from "./BeaconPairingCode";
import DesignStudioTV from "./DesignStudioTV";
import BeaconDemo from "./BeaconDemo";
import BandHowToTV from "./BandHowToTV";
import { useConfig } from "../../../hooks/useConfig";
import { useFairyFlightControls } from "../../../hooks/useFairyFlight";
import useBeaconControl from "../../../hooks/useBeaconControl";
import { useRemote } from "../../../hooks/useRemote";
import useGlobalStore from "../../../store/global.store";

// LIVE Show Sync picker loops (MP4s in public/static/)
const LSS_MULTIVIEW_MP4 = "/static/lss-multiview-tv.jpg";
const LSS_CLOSEUP_MP4 = "/static/lss-closeup-tv.jpg";

/* ============================================================
   MagicBandTV — remote-driven sibling of MagicBand (touch).
   This is the SHELL: tab row, band-firing logic (ported verbatim
   from the touch MagicBand), handoff modal, and it mounts the
   DesignStudioTV wizard. The wizard owns all design navigation;
   the shell owns the tab row + the transmit pipeline + the cross-
   file focus edges (tabs <-> wizard, down -> bottom bar).

   The transmit functions (runTransmit / handlePrepare /
   handleMagicCancel / wake loop / auto-claim / handoff) are the
   SAME proven code as touch — only navigation + sizing differ.
   ============================================================ */

const IDLE_THRESHOLD_MS = 30 * 60 * 1000;
const BG_WAKE_TO_CANCEL_MS = 2000;
const TX_STEP_MS = 500;
const OK_KEYS = new Set(["Enter", " "]);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// DEV ONLY: localStorage wsDevBandPlus = '1' skips the pairing screen so the
// Band+ UI can be tested on a unit with no Beacon. No commands reach a band.
const devBandBypass = () => { try { return localStorage.getItem("wsDevBandPlus") === "1"; } catch (e) { return false; } };

const postWakeSlow = (deviceId) =>
  axios.post(
    "/esp/beacon-show",
    { deviceId, fx: "WAKE_UP_SLOW", vib: "OFF" },
    { withCredentials: true }
  );

const postWakeFast = (deviceId) =>
  axios.post(
    "/esp/beacon-show",
    { deviceId, fx: "WAKE_UP_FAST", vib: "OFF" },
    { withCredentials: true }
  );

const postCancelRaw = (deviceId) =>
  axios.post("/esp/cancel", { deviceId }, { withCredentials: true });

const buildColorPayload = (mode, colors) => {
  if (mode === "1color") {
    return { action: "preset", args: { color: colors.outer }, vib: 0 };
  }
  if (mode === "2color") {
    return { action: "dual", args: { inner: colors.outer, outer: colors.inner }, vib: 0 };
  }
  if (mode === "5color") {
    return {
      action: "rainbow",
      args: { tl: colors.c, tr: colors.tr, c: colors.tl, bl: colors.br, br: colors.bl },
      vib: 0,
    };
  }
  return null;
};

const MagicBandTV = ({ device, onDeviceRefetch }) => {
  const {
    enabled: fairyEnabled,
    vibOn: fairyVib,
    toggleEnabled: toggleFairy,
    toggleVib: toggleFairyVib,
  } = useFairyFlightControls();

  const {
    isController,
    hasHolder,
    holder,
    hasAddon: hasBeaconAddon,
    synced,
    keyReady,
    claim,
    release,
  } = useBeaconControl();
  const { updateView } = useRemote();
  const hasVipAddOn = useGlobalStore((state) => state.hasVipAddOn);
    const { config } = useConfig();
  const [showDemo, setShowDemo] = useState(false);
     const [showHowTo, setShowHowTo] = useState(false);
  // First Band+ visit on this TV: open the tour once.
  useEffect(() => {
    if (!device?.magic_beacon_device && !devBandBypass()) return;
    try {
      if (localStorage.getItem("wsBandHowToSeenTV") !== "1") {
        localStorage.setItem("wsBandHowToSeenTV", "1");
        setShowHowTo(true);
      }
    } catch (e) {}
  }, [device?.magic_beacon_device]);

     const [lssPopup, setLssPopup] = useState(null); // null | "upgrade" | "pick"
  const [lssIndex, setLssIndex] = useState(0);
  const lssRefs = useRef([]);

  // Release flag: config/Buttons { Demo: bool, Demo_Override: [subIds] } —
  // same pattern as BottomBar's buttonEnabled / touch MagicBand.
  const demoEnabled = (() => {
    const b = config?.Buttons;
    if (!b) return false;
    if (b.Demo === true) return true;
    const ov = b.Demo_Override;
    const id = device?.subscription?._id;
    if (!id) return false;
    if (Array.isArray(ov)) return ov.includes(id);
    if (typeof ov === "string") return ov === id;
    return false;
  })();

  const [soleHolderMsg, setSoleHolderMsg] = useState(false);
  const [tinkNoticeMsg, setTinkNoticeMsg] = useState(false);
const tinkNoticeTimerRef = useRef(null);

/* Tink Alert: vibration is always on — no user toggle anymore (parity
   with Display). */
useEffect(() => {
  if (fairyEnabled && !fairyVib) {
    toggleFairyVib();
  }
}, [fairyEnabled, fairyVib, toggleFairyVib]);

// Auto-off: Tink on but another device holds control → turn off (it lost
// control anyway). Gated on synced + keyReady so a booting TV never wipes
// its saved on-state before identity loads.
useEffect(() => {
  if (fairyEnabled && synced && keyReady && hasHolder && !isController) {
    toggleFairy();
    clearTimeout(tinkNoticeTimerRef.current);
    setTinkNoticeMsg(false);
  }
}, [fairyEnabled, synced, keyReady, hasHolder, isController, toggleFairy]);

const showTinkStrip = () => {
  clearTimeout(tinkNoticeTimerRef.current);
  setTinkNoticeMsg(true);
  tinkNoticeTimerRef.current = setTimeout(() => setTinkNoticeMsg(false), 4000);
};

// Toggle: turning ON without control → open the take-control modal in
// "tink" mode. Off, or already controller → just flip.
const handleTinkToggle = () => {
  const turningOn = !fairyEnabled;
  if (turningOn && synced && hasHolder && !isController) {
    setModalMode("tink");
    setPendingTransmit({ tink: true }); // reuse the modal machinery
    return;
  }
  toggleFairy();
  if (turningOn) showTinkStrip();
  else {
    clearTimeout(tinkNoticeTimerRef.current);
    setTinkNoticeMsg(false);
  }
};

const confirmTinkTakeControl = async () => {
  setPendingTransmit(null);
  setModalMode("transmit");
  const r = await claim();
  if (!r.ok) {
    console.error("Tink take-control claim failed:", r.reason);
    return;
  }
  toggleFairy();
  showTinkStrip();
};

const cancelTinkTakeControl = () => {
  setPendingTransmit(null);
  setModalMode("transmit");
  focusTab(lastTabRef.current ?? 0); // remote returns to the tab row
};
  const [pendingTransmit, setPendingTransmit] = useState(null);

  const magic_beacon_device_id = device?.magic_beacon_device?._id;
  const beaconDeviceCount = device?.beacon_device_count ?? 2;

  const deviceIdRef = useRef(magic_beacon_device_id);
  useEffect(() => {
    deviceIdRef.current = magic_beacon_device_id;
  }, [magic_beacon_device_id]);

  /* ====== Auto-claim on first Band+ entry (ported from touch) ====== */
  useEffect(() => {
    if (
      synced &&
      hasBeaconAddon &&
      !hasHolder &&
      !isController &&
      device?.cf_device_name
    ) {
      claim();
    }
  }, [synced, hasBeaconAddon, hasHolder, isController, claim, device?.cf_device_name]);

  // ----- transmit refs (ported verbatim from touch) -----
  const pendingRef = useRef(false);
  const cancelInFlightRef = useRef(false);
  const bgWakeInProgressRef = useRef(false);
  const transmitAbortRef = useRef(false);
  const [transmitting, setTransmitting] = useState(false);
  const wakeFastIntervalRef = useRef(null);
  const containerRef = useRef(null);
  const lastActivityRef = useRef(0);

  const handleTick = useCallback(() => {
    if (pendingRef.current) pendingRef.current = false;
  }, []);

  useIdleState(magic_beacon_device_id, handleTick);

  const fireBackgroundWake = useCallback(async () => {
    if (!isController) return;
    const id = deviceIdRef.current;
    if (!id) return;
    bgWakeInProgressRef.current = true;
    try {
      await postWakeSlow(id);
      await sleep(BG_WAKE_TO_CANCEL_MS);
      if (bgWakeInProgressRef.current) {
        await postCancelRaw(id);
      }
    } catch (e) {
      console.error("Background wake failed:", e);
    } finally {
      bgWakeInProgressRef.current = false;
    }
  }, [isController]);

  const handleActivity = useCallback(() => {
    if (!deviceIdRef.current) return;
    const now = Date.now();
    const idleTime = now - lastActivityRef.current;
    lastActivityRef.current = now;
    if (idleTime > IDLE_THRESHOLD_MS) {
      fireBackgroundWake();
    }
  }, [fireBackgroundWake]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const events = ["click", "touchstart", "keydown"];
    events.forEach((e) =>
      el.addEventListener(e, handleActivity, { passive: true })
    );
    return () =>
      events.forEach((e) => el.removeEventListener(e, handleActivity));
  }, [handleActivity, magic_beacon_device_id]);

  useEffect(() => {
    return () => {
      if (wakeFastIntervalRef.current) {
        clearInterval(wakeFastIntervalRef.current);
        wakeFastIntervalRef.current = null;
      }
    };
  }, []);

  // ----- wake-fast loop (ported) -----
  const startWakeFastLoop = () => {
    if (wakeFastIntervalRef.current) return;
    const id = deviceIdRef.current;
    if (!id) return;
    postWakeFast(id).catch(console.error);
    wakeFastIntervalRef.current = setInterval(() => {
      const did = deviceIdRef.current;
      if (did) postWakeFast(did).catch(console.error);
    }, 3000);
  };
  const stopWakeFastLoop = () => {
    if (wakeFastIntervalRef.current) {
      clearInterval(wakeFastIntervalRef.current);
      wakeFastIntervalRef.current = null;
    }
  };

  // ----- the three callbacks handed to the wizard (ported) -----
  const handlePrepare = () => {
    if (!isController) return;
    bgWakeInProgressRef.current = false;
    transmitAbortRef.current = false;
    startWakeFastLoop();
  };

  const runTransmit = async ({ mode, colors }) => {
    if (transmitting) return;
    stopWakeFastLoop();
    transmitAbortRef.current = false;
    setTransmitting(true);
    const id = magic_beacon_device_id;
    const aborted = () => transmitAbortRef.current;
    try {
      await postCancelRaw(id);
      await sleep(TX_STEP_MS);
      if (aborted()) return;
      const payload = buildColorPayload(mode, colors);
      if (payload) {
        await axios.post(
          "/esp/command",
          { ...payload, deviceId: id },
          { withCredentials: true }
        );
      }
    } catch (e) {
      console.error("Transmit sequence failed:", e);
    } finally {
      setTransmitting(false);
    }
  };

  const handleTransmit = async ({ mode, colors }) => {
  // Returns a verdict for the wizard: "sent" → it runs the countdown;
  // "pending" → take-control modal is up, NO countdown yet;
  // "failed" → nothing sent, wizard returns to selection.
  // runTransmit is deliberately NOT awaited — it runs across the countdown.
  if (transmitting) return "failed";
  if (devBandBypass() && !magic_beacon_device_id) return "sent"; // DEV: fake success so the countdown UI can be tested
  if (isController) {
    runTransmit({ mode, colors });
    return "sent";
  }
  if (hasHolder && holder?.holderDeviceId !== device?._id) {
    setModalMode("transmit");
    setPendingTransmit({ mode, colors });
    return "pending";
  }
  const r = await claim();
  if (r.ok) {
    runTransmit({ mode, colors });
    return "sent";
  }
  console.error("Auto-claim during transmit failed:", r.reason);
  return "failed";
};

  const confirmHandoffAndTransmit = async () => {
    const params = pendingTransmit;
    setPendingTransmit(null);
    if (!params) return;
    const r = await claim();
    if (!r.ok) {
      console.error("Handoff claim failed:", r.reason);
      stopWakeFastLoop();
      if (magic_beacon_device_id)
        postCancelRaw(magic_beacon_device_id).catch(console.error);
      return;
    }
    wizardRef.current?.beginTransmitCountdown?.();
  runTransmit(params);
};

  const cancelHandoff = () => {
  setPendingTransmit(null);
  stopWakeFastLoop();
  if (magic_beacon_device_id)
    postCancelRaw(magic_beacon_device_id).catch(console.error);
  // After the modal unmounts, put the remote back in the color panel
  // (not the bottom bar).
  setTimeout(() => wizardRef.current?.focusEntry?.(), 60);
};

  const handleMagicCancel = () => {
    transmitAbortRef.current = true;
    stopWakeFastLoop();
    if (magic_beacon_device_id)
      postCancelRaw(magic_beacon_device_id).catch(console.error);
  };
 /* LIVE Show Sync — one entry point to the VIP door selector, cloned from
     BottomBar.handleVipClick (holder → "VIP", else upgrade). */
    const handleVipLiveShowSync = () => {
    setLssIndex(0);
    setLssPopup(hasVipAddOn ? "pick" : "upgrade");
  };
  const closeLss = () => {
    setLssPopup(null);
    setTimeout(() => focusTab(lastTabRef.current ?? 0), 60); // remote back to the tab row
  };
    const closeHowTo = () => {
    setShowHowTo(false);
    setTimeout(() => {
      const h = tabs.findIndex((t) => t.key === "howto");
      focusTab(h >= 0 ? h : lastTabRef.current ?? 0);
    }, 60);
  };
   const goLssUpgrade = () => { setLssPopup(null); updateView("VIP__upgrade"); };
  // Same calls as VipBottomBar: door 3 = MultiView, door 2 = Close-Up selector.
  const goLssMultiview = () => { setLssPopup(null); updateView("VIP__multiview"); };
  const goLssCloseup = () => { setLssPopup(null); updateView("VIP_door_2_selector", () => {}); };

  // ===================== TAB ROW + FOCUS =====================
  const wizardRef = useRef(null);
  const tabRefs = useRef([]);
  const lastTabRef = useRef(0);

  // handoff modal focus
  const modalRefs = useRef([]); // [No, Yes]
  const [modalIndex, setModalIndex] = useState(1); // default Yes
  const [modalMode, setModalMode] = useState("transmit"); // "transmit" | "tink"

  // Focusable tab list (built fresh — row length varies by household state)
  const tabs = [];
  const showBeaconButton = hasBeaconAddon && synced && beaconDeviceCount > 1;
  if (showBeaconButton) {
    tabs.push({
      key: "beacon",
      label: `Beacon Control: ${isController ? "Active" : "Inactive"}`,
      cls: isController ? "mbtv-tab-on" : "mbtv-tab-off",
      onOk: async () => {
        if (isController) {
          const r = await release();
          if (r?.reason === "sole_holder") {
            setSoleHolderMsg(true);
            setTimeout(() => setSoleHolderMsg(false), 4000);
          }
        } else {
          await claim();
        }
      },
    });
  }
  tabs.push({
  key: "tink",
  tink: true,
  label: "Tink Alert",
  cls: fairyEnabled ? "mbtv-tab-tink-on" : "",
  onOk: () => handleTinkToggle(),
});
   tabs.push({
    key: "vipSync",
    dot: true,
    label: "VIP Live Show Sync",
    cls: "mbtv-vip-sync",
    onOk: () => handleVipLiveShowSync(),
  });
     tabs.push({
    key: "howto",
    howto: true,
    label: "How To",
    cls: "mbtv-tab-howto",
    onOk: () => setShowHowTo(true),
  });  
   
   if (demoEnabled) {
    tabs.push({
      key: "demo",
      label: "SHOW DEMO",
      cls: "mbtv-tab-demo",
      onOk: () => {
        setShowDemo(true);
        // Blur the tab so the shell key handler goes quiet and the
        // demo's window keydown listener owns the remote.
        setTimeout(() => document.activeElement?.blur?.(), 0);
      },
    });
  }
  const focusTab = (i) => {
    const n = Math.max(0, Math.min(i, tabs.length - 1));
    lastTabRef.current = n;
    tabRefs.current[n]?.focus();
  };

  // If the focused tab vanishes (Fairy OFF removes Vibration), reclamp.
  useEffect(() => {
    if (lastTabRef.current > tabs.length - 1) {
      lastTabRef.current = Math.max(0, tabs.length - 1);
    }
  }, [tabs.length]);

  // Wizard's top edge (Up off mode 0 / interact top row) → tab row.
  const handleExitTopToTabs = () => focusTab(lastTabRef.current ?? 0);

  /* Root key handler. Acts ONLY when the focused element is a tab button
   * (checked via e.target). Everything else — wizard, modal, native bar
   * handoff — is owned elsewhere, so this returns and gets out of the way. */
  const handleShellKey = (e) => {
    const tabPos = tabRefs.current.indexOf(e.target);
    if (tabPos === -1) return;
    const k = e.key;
    if (k === "ArrowLeft") {
      e.preventDefault();
      focusTab(tabPos - 1);
      return;
    }
    if (k === "ArrowRight") {
      e.preventDefault();
      focusTab(tabPos + 1);
      return;
    }
    if (k === "ArrowDown") {
      e.preventDefault();
      wizardRef.current?.focusEntry(); // into the wizard
      return;
    }
    if (k === "ArrowUp") {
      e.preventDefault(); // topmost — stay
      return;
    }
    if (OK_KEYS.has(k)) {
      e.preventDefault();
      tabs[tabPos]?.onOk?.();
      return;
    }
  };
  // ----- LIVE Show Sync popup: focus first button on open, trap keys -----
  useEffect(() => {
    if (!lssPopup) return;
    const t = setTimeout(() => lssRefs.current[0]?.focus(), 60);
    return () => clearTimeout(t);
  }, [lssPopup]);

  const lssActions = lssPopup === "upgrade" ? [goLssUpgrade, closeLss] : [goLssMultiview, goLssCloseup, closeLss];
  const focusLss = (i) => { setLssIndex(i); lssRefs.current[i]?.focus(); };
  const handleLssKey = (e) => {
    const k = e.key;
    e.stopPropagation();
    const isBack = k === "Escape" || k === "GoBack" || k === "BrowserBack" || e.keyCode === 461 || e.keyCode === 10009;
    if (isBack) { e.preventDefault(); closeLss(); return; }
    if (OK_KEYS.has(k)) { e.preventDefault(); lssActions[lssIndex]?.(); return; }
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(k)) return;
    e.preventDefault();
    if (lssPopup === "upgrade") {
      // [UPGRADE NOW!] over [No thanks]
      if (k === "ArrowDown") focusLss(1);
      if (k === "ArrowUp") focusLss(0);
      return;
    }
    // pick: [MultiView] [Close-Up] side by side, [Cancel] below
    if (k === "ArrowLeft" && lssIndex === 1) focusLss(0);
    if (k === "ArrowRight" && lssIndex === 0) focusLss(1);
    if (k === "ArrowDown" && lssIndex < 2) focusLss(2);
    if (k === "ArrowUp" && lssIndex === 2) focusLss(0);
  };
  // ----- handoff modal: focus Yes on open, trap keys -----
  useEffect(() => {
    if (pendingTransmit) {
      setModalIndex(1);
      const t = setTimeout(() => modalRefs.current[1]?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [pendingTransmit]);

  const handleModalKey = (e) => {
    const k = e.key;
    e.stopPropagation();
    if (k === "ArrowLeft") {
      e.preventDefault();
      setModalIndex(0);
      modalRefs.current[0]?.focus();
      return;
    }
    if (k === "ArrowRight") {
      e.preventDefault();
      setModalIndex(1);
      modalRefs.current[1]?.focus();
      return;
    }
    if (k === "ArrowUp" || k === "ArrowDown") {
      e.preventDefault();
      return;
    }
    if (OK_KEYS.has(k)) {
      e.preventDefault();
      if (modalMode === "tink") {
        modalIndex === 1 ? confirmTinkTakeControl() : cancelTinkTakeControl();
      } else {
        modalIndex === 1 ? confirmHandoffAndTransmit() : cancelHandoff();
      }
      return;
    }
    if (k === "Escape") {
      e.preventDefault();
      modalMode === "tink" ? cancelTinkTakeControl() : cancelHandoff();
      return;
    }
  };

  // ===================== DEVICE GUARD =====================
    if (!device?.magic_beacon_device && !devBandBypass()) {
    return (
      <div className="mbtv-container">
        <BeaconSubCheck />
        {hasBeaconAddon ? (
          <BeaconPairingCode
            displayDeviceId={device?._id}
            isTv
            onPaired={onDeviceRefetch}
          />
        ) : (
          <div className="mbtv-empty">
            <h2>Magic Beacon Device not yet setup</h2>
            <p>Please contact support to register your Magic Beacon device.</p>
          </div>
        )}
      </div>
    );
  }

  const otherHolderText =
    hasHolder && holder?.holderDeviceId !== device?._id
      ? `Your Wait Time ${holder.holderDeviceType} (${holder.holderLabel}) has control of your Magic Beacon. Click Beacon Control above to switch.`
      : null;

  return (
    <div className="mbtv-container" ref={containerRef} onKeyDown={handleShellKey}>
      <style>{`
        .mbtv-container{display:flex;flex-direction:column;height:100vh;width:100vw;
          background:#f4f5f8;font-family:"Lato",sans-serif;overflow:hidden;
          box-sizing:border-box;padding-bottom:150px;}
        .mbtv-empty{display:flex;flex-direction:column;align-items:center;
          justify-content:center;height:100%;text-align:center;padding:60px;}
        .mbtv-empty h2{font-size:48px;color:#444;margin-bottom:18px;}
        .mbtv-empty p{font-size:30px;color:#777;}

        .mbtv-tabs{display:flex;gap:18px;justify-content:center;padding:26px 32px;
          background:linear-gradient(180deg,#1e4fa3,#17448f);}
        .mbtv-tab{flex:0 1 auto;min-width:280px;padding:20px 40px;border-radius:16px;
          border:4px solid rgba(255,255,255,0.25);background:rgba(255,255,255,0.10);
          color:#fff;font-size:34px;font-weight:800;font-family:"Lato",sans-serif;
          cursor:pointer;text-align:center;outline:none;
          transition:transform .12s ease, box-shadow .12s ease;}
        .mbtv-tab:focus{border-color:#05f651;
          box-shadow:0 0 0 4px #05f651, 0 0 28px rgba(5,246,81,.5);transform:scale(1.04);}
        .mbtv-tab-on{background:linear-gradient(110deg,#059669,#10b981,#059669)!important;}
        .mbtv-tab-off{background:linear-gradient(180deg,#dc2626,#991b1b)!important;}
        .mbtv-tab-active{background:linear-gradient(135deg,#f5e6c8,#ede0c8)!important;color:#1a2542!important;}
        .mbtv-tab-disabled{opacity:.45;cursor:default;}
        .mbtv-tab-demo{background:linear-gradient(180deg,#7c3aed,#5b21b6)!important;
          box-shadow:0 2px 8px rgba(91,33,182,0.40),inset 0 1px 0 rgba(255,255,255,0.18);}

        .mbtv-strip{padding:14px 24px;text-align:center;font-size:24px;font-weight:700;
          font-family:"Lato",sans-serif;}
        .mbtv-strip-other{background:linear-gradient(180deg,#fef2f2,#fee2e2);color:#991b1b;
          border-bottom:1px solid #fecaca;}
        .mbtv-strip-sole{background:#fef3c7;color:#92400e;border-bottom:1px solid #fde68a;}
        .mbtv-strip-tink{background:linear-gradient(180deg,#f0fdf4,#dcfce7);color:#166534;
  border-bottom:1px solid #bbf7d0;font-size:28px;}
.mbtv-tab-tink-on{
  background-image:linear-gradient(100deg,#1db954,#7ed957,#ffd93b,#ffe9a8,#ffd93b,#7ed957,#1db954)!important;
  background-size:200% 100%!important;
  background-repeat:repeat!important;
  animation:mbtvTinkShimmer 6s linear infinite!important;
  color:#fff!important;text-shadow:0 1px 6px rgba(0,0,0,0.35);}
@keyframes mbtvTinkShimmer{0%{background-position:0% 50%;}100%{background-position:200% 50%;}}
.mbtv-tink-inner{display:flex;align-items:center;justify-content:center;gap:14px;}
.mbtv-tink-switch{position:relative;width:64px;height:34px;border-radius:17px;
  background:rgba(255,255,255,0.25);transition:background .2s ease;flex:0 0 auto;}
.mbtv-tab-tink-on .mbtv-tink-switch{background:rgba(8,61,30,0.45);}
.mbtv-tink-knob{position:absolute;top:4px;left:4px;width:26px;height:26px;border-radius:50%;
  background:#fff;transition:left .2s ease;}
.mbtv-tab-tink-on .mbtv-tink-knob{left:34px;}
.mbtv-tab.mbtv-vip-sync{color:#fff!important;text-shadow:0 1px 6px rgba(0,0,0,0.5);
  border:4px solid transparent;
  background-image:linear-gradient(rgba(8,10,24,.35),rgba(8,10,24,.35)),
    url(/beacon-promo/night.jpg),
    linear-gradient(90deg,#FF5C93,#FF8A3D,#FFD23D,#3EE08A,#3CC9FF,#8F7CFF,#FF5C93,#FF8A3D,#FFD23D,#3EE08A,#3CC9FF,#8F7CFF,#FF5C93)!important;
  background-origin:border-box!important;
  background-clip:padding-box,padding-box,border-box!important;
  background-size:auto,cover,300% 100%!important;
  background-repeat:no-repeat,no-repeat,repeat!important;
  animation:mbtvVipEdge 7s linear infinite;
  box-shadow:0 2px 10px rgba(0,0,0,0.4);}
.mbtv-tab.mbtv-vip-sync:focus{border-color:transparent!important;
  box-shadow:0 0 0 4px #05f651,0 0 30px rgba(5,246,81,.55);transform:scale(1.04);}
@keyframes mbtvVipEdge{0%{background-position:0 0,center,0% 50%;}100%{background-position:0 0,center,300% 50%;}}
.mbtv-vip-inner{display:flex;align-items:center;justify-content:center;gap:14px;}
.mbtv-live-dot{flex:0 0 auto;width:16px;height:16px;border-radius:50%;background:#ff2d2d;
  box-shadow:0 0 8px rgba(255,45,45,0.85);animation:mbtvLiveDotPulse 1.6s ease-in-out infinite;}
@keyframes mbtvLiveDotPulse{0%,100%{opacity:1;transform:scale(1);}50%{opacity:.45;transform:scale(.75);}}

/* fireworks: each <i> is a spark that shoots out + fades, staggered.
   No blur — pure transform/opacity, cheap on the panel. */
.mbtv-vip-inner{display:flex;align-items:center;justify-content:center;gap:16px;}
.mbtv-vip-label{position:relative;z-index:2;}
.mbtv-fw{position:relative;width:34px;height:34px;flex:0 0 auto;z-index:1;}
.mbtv-fw i{position:absolute;top:50%;left:50%;width:5px;height:5px;border-radius:50%;
  margin:-2.5px 0 0 -2.5px;opacity:0;
  box-shadow:0 0 6px rgba(255,255,255,0.9);
  animation:mbtvSpark 2.4s ease-out infinite;}
.mbtv-fw i:nth-child(1){background:#ffd93b;--dx:13px;--dy:-11px;animation-delay:0s;}
.mbtv-fw i:nth-child(2){background:#ec4899;--dx:-12px;--dy:-9px;animation-delay:.35s;}
.mbtv-fw i:nth-child(3){background:#a855f7;--dx:14px;--dy:8px;animation-delay:.7s;}
.mbtv-fw i:nth-child(4){background:#38bdf8;--dx:-13px;--dy:10px;animation-delay:1.05s;}
.mbtv-fw i:nth-child(5){background:#fff;--dx:6px;--dy:-15px;animation-delay:1.4s;}
.mbtv-fw i:nth-child(6){background:#ffd93b;--dx:-7px;--dy:14px;animation-delay:1.75s;}
.mbtv-fw-right i:nth-child(odd){animation-delay:.5s;}
@keyframes mbtvSpark{
  0%{opacity:0;transform:translate(0,0) scale(.4);}
  12%{opacity:1;transform:translate(calc(var(--dx)*.35),calc(var(--dy)*.35)) scale(1);}
  60%{opacity:.9;transform:translate(var(--dx),var(--dy)) scale(.85);}
  100%{opacity:0;transform:translate(calc(var(--dx)*1.25),calc(var(--dy)*1.25)) scale(.2);}
}
        .mbtv-tab.mbtv-tab-howto{background:#fff!important;color:#007bff!important;border-color:#fff;}
        .mbtv-tab.mbtv-tab-howto:focus{border-color:#05f651;}
        .mbtv-howto-inner{display:flex;align-items:center;justify-content:center;gap:14px;}
        .mbtv-howto-ico{width:38px;height:38px;border-radius:50%;background:#007bff;color:#fff;font-size:26px;font-weight:900;display:flex;align-items:center;justify-content:center;line-height:1;}
        .mbtv-stage{flex:1;padding:34px 40px;min-height:0;display:block;}

        /* Handoff modal */
        .mbtv-modal-back{position:fixed;inset:0;background:rgba(0,0,0,.62);z-index:10000;
          display:flex;align-items:center;justify-content:center;}
        .mbtv-modal{background:#fff;border-radius:20px;padding:44px 40px 36px;max-width:720px;
          width:calc(100% - 80px);text-align:center;font-family:"Lato",sans-serif;
          box-shadow:0 16px 50px rgba(0,0,0,.5);}
        .mbtv-modal h3{margin:0 0 16px;font-size:40px;color:#1f2937;font-weight:900;}
        .mbtv-modal p{margin:0 0 32px;font-size:26px;color:#4b5563;line-height:1.4;}
        .mbtv-modal-btns{display:flex;gap:18px;justify-content:center;}
        .mbtv-modal-btn{flex:1;padding:20px 28px;border-radius:14px;border:4px solid transparent;
          font-family:"Lato",sans-serif;font-size:28px;font-weight:900;cursor:pointer;outline:none;
          transition:transform .12s ease, box-shadow .12s ease;}
        .mbtv-modal-btn:focus{border-color:#05f651;transform:scale(1.06);}
        .mbtv-modal-no{background:#e5e7eb;color:#374151;border-color:#9ca3af;}
        .mbtv-modal-yes{background:#007bff;color:#fff;}
                .mbtv-lss-back{position:fixed;inset:0;background:rgba(6,7,16,.85);z-index:10000;display:flex;align-items:center;justify-content:center;}
        .mbtv-lss-card{max-width:92vw;border:5px solid transparent;border-radius:28px;padding:44px 48px 34px;text-align:center;font-family:"Lato",sans-serif;color:#fff;box-shadow:0 20px 60px rgba(0,0,0,.6);background-image:linear-gradient(rgba(8,10,24,.55),rgba(8,10,24,.55)),url(/beacon-promo/night.jpg),linear-gradient(90deg,#FF5C93,#FF8A3D,#FFD23D,#3EE08A,#3CC9FF,#8F7CFF,#FF5C93,#FF8A3D,#FFD23D,#3EE08A,#3CC9FF,#8F7CFF,#FF5C93);background-origin:border-box;background-clip:padding-box,padding-box,border-box;background-size:auto,cover,300% 100%;background-position:center,center,0% 50%;background-repeat:no-repeat,no-repeat,repeat;animation:mbtvLssRing 7s linear infinite;}
        @keyframes mbtvLssRing{from{background-position:center,center,0% 50%}to{background-position:center,center,300% 50%}}        .mbtv-lss-title{display:flex;align-items:center;justify-content:center;gap:16px;margin:0 0 14px;font-size:52px;font-weight:900;color:#fff;}
        .mbtv-lss-sub{margin:0 0 34px;font-size:30px;font-weight:600;color:rgba(255,255,255,.88);line-height:1.4;}
                .mbtv-lss-upgrade{display:block;width:100%;padding:26px 30px;border:5px solid #fff;border-radius:18px;background:rgba(255,255,255,.12);color:#fff;font-family:"Lato",sans-serif;font-size:38px;font-weight:900;letter-spacing:1px;cursor:pointer;outline:none;transition:transform .12s ease,box-shadow .12s ease;}
        .mbtv-lss-no{margin-top:22px;padding:12px 28px;border:4px solid transparent;border-radius:14px;background:none;color:rgba(255,255,255,.8);font-family:"Lato",sans-serif;font-size:28px;font-weight:700;text-decoration:underline;cursor:pointer;outline:none;transition:transform .12s ease,box-shadow .12s ease;}
        .mbtv-lss-opts{display:flex;gap:34px;justify-content:center;}
        .mbtv-lss-opt{width:min(620px,42vw);padding:0;border:5px solid rgba(255,255,255,.3);border-radius:22px;background:#12051f;overflow:hidden;cursor:pointer;color:#fff;font-family:"Lato",sans-serif;text-align:center;outline:none;transition:transform .12s ease,box-shadow .12s ease;}
        .mbtv-lss-gif{display:block;width:100%;aspect-ratio:16/9;object-fit:cover;background:#000;}
        .mbtv-lss-opt-name{display:block;padding:20px 16px 6px;font-size:36px;font-weight:900;}
        .mbtv-lss-opt-desc{display:block;padding:0 20px 22px;font-size:24px;font-weight:600;color:rgba(255,255,255,.82);line-height:1.35;}
        .mbtv-lss-upgrade:focus,.mbtv-lss-opt:focus,.mbtv-lss-no:focus{border-color:#05f651;box-shadow:0 0 0 4px #05f651,0 0 30px rgba(5,246,81,.5);transform:scale(1.04);}
        .mbtv-lss-close{display:block;margin:28px auto 0;padding:18px 70px;border:4px solid rgba(255,255,255,.55);border-radius:16px;background:rgba(255,255,255,.12);color:#fff;font-family:"Lato",sans-serif;font-size:30px;font-weight:900;letter-spacing:1px;cursor:pointer;outline:none;transition:transform .12s ease,box-shadow .12s ease;}
        .mbtv-lss-close:focus{border-color:#05f651;box-shadow:0 0 0 4px #05f651,0 0 30px rgba(5,246,81,.5);transform:scale(1.04);}
      `}</style>

      <BeaconSubCheck />

      {/* ===== TAB ROW ===== */}
      <div className="mbtv-tabs">
        {tabs.map((t, i) => (
          <button
            key={t.key}
            ref={(el) => (tabRefs.current[i] = el)}
            className={`mbtv-tab ${t.cls}`}
            tabIndex={0}
            onFocus={() => {
              lastTabRef.current = i;
            }}
            onClick={() => t.onOk?.()}
          >
            {t.tink ? (
  <span className="mbtv-tink-inner">
    <svg className="tink-bell" viewBox="0 0 24 24" width="30" height="30"
         fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
    Tink Alert
    <span className="mbtv-tink-switch"><span className="mbtv-tink-knob" /></span>
  </span>
) : t.howto ? (
  <span className="mbtv-howto-inner">
    <span className="mbtv-howto-ico">?</span>
    {t.label}
  </span>
) : t.dot ? (
  <span className="mbtv-vip-inner">
    <span className="mbtv-live-dot" />
    {t.label}
  </span>
) : (
  t.label
)}
          </button>
        ))}
        
      </div>

            {/* status strips — wrapped so the Timer's own CSS can float them
          (overlay) while it's mounted; everywhere else they push as normal. */}
      <div className="mbtv-strips">
      {showBeaconButton && !isController && otherHolderText && (
        <div className="mbtv-strip mbtv-strip-other">{otherHolderText}</div>
      )}
      {soleHolderMsg && (
        <div className="mbtv-strip mbtv-strip-sole">
          At least one device must control the Magic Beacon. Turn on control on
          another device to switch.
        </div>
      )}
      {tinkNoticeMsg && (
  <div className="mbtv-strip mbtv-strip-tink">
    Your bands will illuminate when Tink flies over the castle!
  </div>
)}
      </div>

            {/* ===== WIZARD ===== */}
      {!showDemo && (
      <div className="mbtv-stage">
        <DesignStudioTV
          ref={wizardRef}
          device={device}
          onPrepare={handlePrepare}
          onTransmit={handleTransmit}
          onCancel={handleMagicCancel}
          onExitTop={handleExitTopToTabs}
        />
      </div>
      )}

      {/* ===== HANDOFF MODAL ===== */}
      {pendingTransmit && hasHolder && holder?.holderDeviceId !== device?._id && (
        <div
  className="mbtv-modal-back"
  onKeyDown={handleModalKey}
  onBlurCapture={(e) => {
    // Anything that pulls focus out of the modal (e.g. the wizard's
    // late swatch refocus) → yank it back to the selected button.
    if (!e.currentTarget.contains(e.relatedTarget)) {
      modalRefs.current[modalIndex]?.focus();
    }
  }}
>
          <div className="mbtv-modal">
            <h3>Take Control of Your Magic Beacon?</h3>
            <p>
              Your Wait Time {holder.holderDeviceType}: {holder.holderLabel} is
              currently controlling the beacon.
              {modalMode === "tink"
                ? " Tink Alert needs control to light your bands. Switch control to this device?"
                : " Switch control to this device?"}
            </p>
            <div className="mbtv-modal-btns">
              <button
                ref={(el) => (modalRefs.current[0] = el)}
                className="mbtv-modal-btn mbtv-modal-no"
                tabIndex={0}
                onFocus={() => setModalIndex(0)}
                onClick={modalMode === "tink" ? cancelTinkTakeControl : cancelHandoff}
              >
                No
              </button>
              <button
                ref={(el) => (modalRefs.current[1] = el)}
                className="mbtv-modal-btn mbtv-modal-yes"
                tabIndex={0}
                onFocus={() => setModalIndex(1)}
                onClick={modalMode === "tink" ? confirmTinkTakeControl : confirmHandoffAndTransmit}
              >
                Yes, Switch
              </button>
            </div>
          </div>
        </div>
      )}
             {lssPopup && (
        <div
          className="mbtv-lss-back"
          onKeyDown={handleLssKey}
          onBlurCapture={(e) => {
            // keep the remote inside the popup
            if (!e.currentTarget.contains(e.relatedTarget)) lssRefs.current[lssIndex]?.focus();
          }}
        >
          {lssPopup === "upgrade" ? (
            <div className="mbtv-lss-card" style={{ width: 840 }}>
              <h3 className="mbtv-lss-title"><span className="mbtv-live-dot" />LIVE Show Sync</h3>
              <p className="mbtv-lss-sub">VIP Package is required for LIVE Show Sync.</p>
              <button ref={(el) => (lssRefs.current[0] = el)} className="mbtv-lss-upgrade" tabIndex={0} onFocus={() => setLssIndex(0)} onClick={goLssUpgrade}>UPGRADE NOW!</button>
              <button ref={(el) => (lssRefs.current[1] = el)} className="mbtv-lss-no" tabIndex={0} onFocus={() => setLssIndex(1)} onClick={closeLss}>No thanks</button>
            </div>
          ) : (
            <div className="mbtv-lss-card">
              <h3 className="mbtv-lss-title"><span className="mbtv-live-dot" />LIVE Show Sync</h3>
              <p className="mbtv-lss-sub">Pick a LIVE show. Your bands sync to it automatically!</p>
              <div className="mbtv-lss-opts">
                <button ref={(el) => (lssRefs.current[0] = el)} className="mbtv-lss-opt" tabIndex={0} onFocus={() => setLssIndex(0)} onClick={goLssMultiview}>
                  <img className="mbtv-lss-gif" src={LSS_MULTIVIEW_MP4} alt="" draggable={false} />                  <span className="mbtv-lss-opt-name">Magical MultiView</span>
                  <span className="mbtv-lss-opt-desc">Watch LIVE streamers in the parks right now</span>
                </button>
                <button ref={(el) => (lssRefs.current[1] = el)} className="mbtv-lss-opt" tabIndex={0} onFocus={() => setLssIndex(1)} onClick={goLssCloseup}>
                  <img className="mbtv-lss-gif" src={LSS_CLOSEUP_MP4} alt="" draggable={false} />                  <span className="mbtv-lss-opt-name">LIVE Close-Up Fireworks</span>
                  <span className="mbtv-lss-opt-desc">Our dedicated fireworks camera, front and center</span>
                </button>
              </div>
              <button ref={(el) => (lssRefs.current[2] = el)} className="mbtv-lss-close" tabIndex={0} onFocus={() => setLssIndex(2)} onClick={closeLss}>Close</button>            </div>
          )}
        </div>
      )}  
             {showHowTo && <BandHowToTV onClose={closeHowTo} />}
       {showDemo && (
        <BeaconDemo
          device={device}
          isTv={true}
          onExit={() => {
            setShowDemo(false);
            // Remote returns to the tab row (same pattern as the modals).
            setTimeout(() => focusTab(lastTabRef.current ?? 0), 60);
          }}
        />
      )}
    </div>
  );
};

export default MagicBandTV;
